// Review cycles on a Pursuit (SAD-487 / CAP-03).
//
// A Review is a session with a purpose, date, participants, areas and a
// readiness call. It stands on its own: creating one never records a go/no-go
// decision (it may optionally point at one). Materials are server-side
// snapshots — the text shown is what was discussed, never the live source — and
// a closed review refuses further findings and materials. Artifact materials are
// not implemented server-side, so only the opportunity hypothesis is offered.
// A caller who cannot read the Pursuit sees no titles, text or counts.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraBadge, SpecraButton, SpecraCard, SpecraCardContent, SpecraCardHeader, SpecraCardTitle, SpecraCheckbox, SpecraCheckboxField, SpecraEmptyState, SpecraFormField, SpecraInput, SpecraOverlayDialog, SpecraSelect, SpecraText, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { format, parseISO } from 'date-fns';
import { useState } from 'react';
import { PlatformUserPicker } from '../Delivery/_shared/components/PlatformUserPicker';
import { useGoNoGoRecords } from './goNoGoHooks';
import { useCapturePlan } from './hooks';
import { ReadOnlyScope } from './record/ReadOnlyScope';
import { useAddReviewFinding, useAddReviewMaterial, useCaptureReview, useCaptureReviews, useCloseCaptureReview, useCreateCaptureReview } from './reviewHooks';
const READINESS_OPTIONS = [
    {
        value: 'not_assessed',
        label: 'Not assessed'
    },
    {
        value: 'not_ready',
        label: 'Not ready'
    },
    {
        value: 'partially_ready',
        label: 'Partially ready'
    },
    {
        value: 'ready',
        label: 'Ready'
    }
];
const OUTCOME_LABEL = {
    go: 'Go',
    no_go: 'No-Go',
    defer: 'Defer'
};
const READINESS_LABEL = Object.fromEntries(READINESS_OPTIONS.map((o)=>[
        o.value,
        o.label
    ]));
function errorText(err) {
    return err instanceof Error ? err.message : 'Something went wrong';
}
function isDenied(err) {
    return err instanceof ConnectError && err.code === Code.PermissionDenied;
}
function splitList(text) {
    return text.split(',').map((s)=>s.trim()).filter(Boolean);
}
function shortHash(sha) {
    return sha.slice(0, 12);
}
// specra-allow local-composite: One review with its frozen materials, findings and closure controls.
function ReviewRow({ captureProjectId, review, linkedOutcome, onRecordDecision }) {
    const detail = useCaptureReview(captureProjectId, review.id);
    const attach = useAddReviewMaterial();
    const addFinding = useAddReviewFinding();
    const close = useCloseCaptureReview();
    const [finding, setFinding] = useState('');
    const [summary, setSummary] = useState('');
    const [error, setError] = useState(null);
    const closed = review.status === 'closed';
    const materials = detail.data?.materials ?? [];
    const findings = detail.data?.findings ?? [];
    const busy = attach.isPending || addFinding.isPending || close.isPending;
    const onError = (err)=>setError(errorText(err));
    const onAttach = ()=>{
        setError(null);
        attach.mutate({
            captureProjectId,
            reviewId: review.id
        }, {
            onError
        });
    };
    const onAddFinding = ()=>{
        setError(null);
        addFinding.mutate({
            captureProjectId,
            reviewId: review.id,
            materialId: '',
            severity: '',
            body: finding.trim()
        }, {
            onSuccess: ()=>setFinding(''),
            onError
        });
    };
    const onClose = ()=>{
        setError(null);
        close.mutate({
            captureProjectId,
            reviewId: review.id,
            closureSummary: summary.trim()
        }, {
            onError
        });
    };
    return /*#__PURE__*/ _jsxs("li", {
        className: "space-y-3 rounded-md border border-border p-3",
        "data-testid": Testids.captureReviews.row,
        "data-review-id": review.id,
        "data-status": review.status,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-2",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm font-medium",
                        children: review.title
                    }),
                    /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: closed ? 'neutral' : 'success',
                        children: closed ? 'Closed' : 'Open'
                    }),
                    /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: "info",
                        children: READINESS_LABEL[review.readiness] ?? review.readiness
                    }),
                    review.kind === 'gate' && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(SpecraBadge, {
                                variant: review.goNoGoRecordId ? 'success' : 'warning',
                                children: review.goNoGoRecordId ? 'Decided' : 'Gate'
                            }),
                            /*#__PURE__*/ _jsx(SpecraText, {
                                size: "meta",
                                tone: "muted",
                                children: review.goNoGoRecordId ? linkedOutcome : `due ${review.reviewDate ? format(parseISO(review.reviewDate), 'd MMM') : '—'}${review.deciderDisplayName ? ` · ${review.deciderDisplayName}` : ''}`
                            }),
                            !closed && !review.goNoGoRecordId && onRecordDecision && /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "xs",
                                variant: "secondary",
                                onClick: ()=>onRecordDecision(review.id),
                                "data-testid": Testids.capture.reviewRecordDecision,
                                children: "Record decision"
                            })
                        ]
                    }),
                    review.kind !== 'gate' && review.reviewDate && /*#__PURE__*/ _jsx("span", {
                        className: "text-xs text-muted-foreground",
                        children: review.reviewDate
                    })
                ]
            }),
            review.purpose && /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-muted-foreground",
                children: review.purpose
            }),
            (review.participants.length > 0 || review.areaKeys.length > 0) && /*#__PURE__*/ _jsxs("p", {
                className: "text-xs text-muted-foreground",
                children: [
                    review.participants.length > 0 && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            "Participants: ",
                            review.participants.join(', '),
                            ". "
                        ]
                    }),
                    review.areaKeys.length > 0 && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            "Areas: ",
                            review.areaKeys.join(', '),
                            "."
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-xs font-medium",
                        children: "Materials (as discussed)"
                    }),
                    detail.isLoading && /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-muted-foreground",
                        children: "Loading…"
                    }),
                    detail.error && /*#__PURE__*/ _jsxs("p", {
                        className: "text-sm text-danger",
                        role: "alert",
                        children: [
                            "Couldn't load this review: ",
                            errorText(detail.error)
                        ]
                    }),
                    materials.map((m)=>/*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1 rounded-md bg-muted p-2",
                            "data-testid": Testids.captureReviews.materialRow,
                            "data-material-id": m.id,
                            "data-source-kind": m.sourceKind,
                            "data-sha256": m.contentSha256,
                            children: [
                                /*#__PURE__*/ _jsxs("p", {
                                    className: "text-xs text-muted-foreground",
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            "data-testid": Testids.captureReviews.materialVersion,
                                            children: m.versionLabel || m.sourceVersion
                                        }),
                                        ' ',
                                        "· sha256 ",
                                        shortHash(m.contentSha256)
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "whitespace-pre-wrap text-sm",
                                    "data-testid": Testids.captureReviews.materialContent,
                                    children: m.contentSnapshot
                                })
                            ]
                        }, m.id)),
                    !closed && /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "secondary",
                        onClick: onAttach,
                        disabled: busy,
                        "data-testid": Testids.captureReviews.attachMaterialButton,
                        children: "Attach opportunity hypothesis"
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-xs font-medium",
                        children: "Findings"
                    }),
                    findings.length === 0 && !detail.isLoading && /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-muted-foreground",
                        children: "No findings recorded."
                    }),
                    /*#__PURE__*/ _jsx("ul", {
                        className: "space-y-1",
                        children: findings.map((f)=>/*#__PURE__*/ _jsxs("li", {
                                className: "text-sm",
                                "data-testid": Testids.captureReviews.findingRow,
                                "data-finding-id": f.id,
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraBadge, {
                                        variant: "neutral",
                                        children: f.severity || 'note'
                                    }),
                                    " ",
                                    f.body
                                ]
                            }, f.id))
                    }),
                    /*#__PURE__*/ _jsx(SpecraTextarea, {
                        value: finding,
                        onChange: (e)=>setFinding(e.target.value),
                        placeholder: closed ? 'This review is closed' : 'What did the review find?',
                        disabled: closed || busy,
                        "aria-label": "New finding",
                        "data-testid": Testids.captureReviews.findingInput
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        onClick: onAddFinding,
                        disabled: closed || busy || finding.trim() === '',
                        "data-testid": Testids.captureReviews.addFindingButton,
                        children: "Add finding"
                    })
                ]
            }),
            closed ? /*#__PURE__*/ _jsxs("p", {
                className: "text-sm",
                "data-testid": Testids.captureReviews.closedSummary,
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "font-medium",
                        children: "Closure summary:"
                    }),
                    " ",
                    review.closureSummary
                ]
            }) : /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraTextarea, {
                        value: summary,
                        onChange: (e)=>setSummary(e.target.value),
                        placeholder: "Closure summary",
                        disabled: busy,
                        "aria-label": "Closure summary",
                        "data-testid": Testids.captureReviews.closureSummaryInput
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "secondary",
                        onClick: onClose,
                        disabled: busy || summary.trim() === '',
                        "data-testid": Testids.captureReviews.closeButton,
                        children: "Close review"
                    })
                ]
            }),
            error && /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-danger",
                role: "alert",
                children: error
            })
        ]
    });
}
export function CaptureReviewsPanel({ captureProjectId, readOnly = false, onRecordDecision, open: openProp, onOpenChange }) {
    const [ownOpen, setOwnOpen] = useState(false);
    const controlled = onOpenChange !== undefined;
    const open = controlled ? !!openProp : ownOpen;
    const setOpen = onOpenChange ?? setOwnOpen;
    const reviews = useCaptureReviews(captureProjectId);
    const records = useGoNoGoRecords(captureProjectId);
    const create = useCreateCaptureReview();
    const [isGate, setIsGate] = useState(false);
    const [deciderUserId, setDeciderUserId] = useState('');
    const [dateError, setDateError] = useState(false);
    const [title, setTitle] = useState('');
    const [purpose, setPurpose] = useState('');
    const [reviewDate, setReviewDate] = useState('');
    const [participants, setParticipants] = useState('');
    // Review area keys are stored as free text and shown as-is, so a pick stores the
    // Capture Plan area's name (old reviews typed names too).
    const [areas, setAreas] = useState([]);
    const plan = useCapturePlan(captureProjectId);
    const planAreas = plan.data?.areas ?? [];
    const [readiness, setReadiness] = useState('not_assessed');
    const [goNoGoRecordId, setGoNoGoRecordId] = useState('');
    const [createError, setCreateError] = useState(null);
    const denied = isDenied(reviews.error) || create.isError && isDenied(create.error);
    const loadError = !denied && reviews.error ? errorText(reviews.error) : '';
    const list = denied ? [] : reviews.data ?? [];
    const linkable = records.data ?? [];
    const canAdd = !readOnly && !denied;
    const onCreate = ()=>{
        setCreateError(null);
        setDateError(isGate && !reviewDate);
        if (isGate && !reviewDate) return;
        create.mutate({
            captureProjectId,
            fields: {
                title: title.trim(),
                purpose: purpose.trim(),
                reviewDate,
                participants: splitList(participants),
                areaKeys: areas,
                readiness,
                goNoGoRecordId,
                kind: isGate ? 'gate' : 'review',
                deciderUserId: isGate ? deciderUserId : ''
            }
        }, {
            onSuccess: ()=>{
                setOpen(false);
                setIsGate(false);
                setDeciderUserId('');
                setDateError(false);
                setTitle('');
                setPurpose('');
                setReviewDate('');
                setParticipants('');
                setAreas([]);
                setReadiness('not_assessed');
                setGoNoGoRecordId('');
            },
            onError: (err)=>{
                if (!isDenied(err)) setCreateError(errorText(err));
            }
        });
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(ReadOnlyScope, {
                readOnly: readOnly,
                children: /*#__PURE__*/ _jsxs(SpecraCard, {
                    "data-testid": Testids.captureReviews.panel,
                    children: [
                        /*#__PURE__*/ _jsxs(SpecraCardHeader, {
                            children: [
                                /*#__PURE__*/ _jsx(SpecraCardTitle, {
                                    children: "Reviews"
                                }),
                                canAdd && !controlled && list.length > 0 && /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    onClick: ()=>setOpen(true),
                                    "data-testid": Testids.pursuitDecisions.addReviewButton,
                                    children: "Add review"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(SpecraCardContent, {
                            className: "space-y-4",
                            children: denied ? /*#__PURE__*/ _jsx("p", {
                                className: "rounded-md border border-warning/25 bg-warning-muted p-3 text-sm text-warning",
                                children: "You don't have permission to see reviews on this Pursuit. Ask a Growth owner to grant you access."
                            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-sm text-muted-foreground",
                                        children: "Run a review whenever the team meets — no go/no-go decision is needed. Materials are kept as discussed, so later edits to the source never change what a review shows."
                                    }),
                                    loadError && /*#__PURE__*/ _jsxs("p", {
                                        className: "text-sm text-danger",
                                        role: "alert",
                                        children: [
                                            "Couldn't load reviews: ",
                                            loadError
                                        ]
                                    }),
                                    reviews.isLoading ? /*#__PURE__*/ _jsx("p", {
                                        className: "text-sm text-muted-foreground",
                                        children: "Loading reviews…"
                                    }) : list.length === 0 && !loadError ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                                        size: "compact",
                                        title: "No reviews yet.",
                                        description: "Run one whenever the team meets.",
                                        "data-testid": Testids.pursuitDecisions.reviewsEmptyState,
                                        action: canAdd ? /*#__PURE__*/ _jsx(SpecraButton, {
                                            size: "sm",
                                            onClick: ()=>setOpen(true),
                                            "data-testid": Testids.pursuitDecisions.addReviewButton,
                                            children: "Add review"
                                        }) : undefined
                                    }) : /*#__PURE__*/ _jsx("ul", {
                                        className: "space-y-3",
                                        children: list.map((r)=>/*#__PURE__*/ _jsx(ReviewRow, {
                                                captureProjectId: captureProjectId,
                                                review: r,
                                                linkedOutcome: OUTCOME_LABEL[linkable.find((record)=>record.id === r.goNoGoRecordId)?.outcome ?? ''],
                                                onRecordDecision: readOnly ? undefined : onRecordDecision
                                            }, r.id))
                                    })
                                ]
                            })
                        })
                    ]
                })
            }),
            canAdd && /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
                open: open,
                onOpenChange: setOpen,
                title: "Add review",
                size: "lg",
                testId: Testids.pursuitDecisions.reviewDialog,
                footerProps: {
                    onCancel: ()=>setOpen(false),
                    confirmLabel: 'Create review',
                    onConfirm: onCreate,
                    confirmProps: {
                        disabled: create.isPending || title.trim() === '',
                        'data-testid': Testids.captureReviews.createButton
                    }
                },
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-3",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "Review title",
                            htmlFor: "capture-review-title",
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                id: "capture-review-title",
                                value: title,
                                onChange: (e)=>setTitle(e.target.value),
                                placeholder: isGate ? 'Gate 2' : 'e.g. Pink team review',
                                disabled: create.isPending,
                                "data-testid": Testids.captureReviews.titleInput
                            })
                        }),
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "Purpose",
                            htmlFor: "capture-review-purpose",
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                id: "capture-review-purpose",
                                value: purpose,
                                onChange: (e)=>setPurpose(e.target.value),
                                disabled: create.isPending
                            })
                        }),
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "This is a go/no-go gate",
                            htmlFor: "capture-review-is-gate",
                            children: /*#__PURE__*/ _jsx(SpecraCheckbox, {
                                id: "capture-review-is-gate",
                                checked: isGate,
                                onCheckedChange: (checked)=>{
                                    setIsGate(checked === true);
                                    setDateError(false);
                                },
                                disabled: create.isPending,
                                "data-testid": Testids.capture.reviewIsGate
                            })
                        }),
                        isGate && /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "Decider",
                            htmlFor: "capture-review-decider",
                            children: /*#__PURE__*/ _jsx(PlatformUserPicker, {
                                id: "capture-review-decider",
                                value: deciderUserId,
                                onChange: setDeciderUserId,
                                testid: Testids.capture.reviewDeciderSearch,
                                disabled: create.isPending,
                                placeholder: "Search people…",
                                clearLabel: "No one"
                            })
                        }),
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "Date",
                            htmlFor: "capture-review-date",
                            required: isGate,
                            error: dateError ? 'A gate needs a date' : undefined,
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                id: "capture-review-date",
                                type: "date",
                                value: reviewDate,
                                onChange: (e)=>{
                                    setReviewDate(e.target.value);
                                    setDateError(false);
                                },
                                required: isGate,
                                disabled: create.isPending
                            })
                        }),
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "Participants (comma separated)",
                            htmlFor: "capture-review-people",
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                id: "capture-review-people",
                                value: participants,
                                onChange: (e)=>setParticipants(e.target.value),
                                disabled: create.isPending
                            })
                        }),
                        /*#__PURE__*/ _jsxs("fieldset", {
                            className: "space-y-1.5",
                            "data-testid": Testids.captureReviews.areasPicker,
                            children: [
                                /*#__PURE__*/ _jsx("legend", {
                                    className: "text-xs font-medium",
                                    children: "Areas covered"
                                }),
                                planAreas.length === 0 ? /*#__PURE__*/ _jsx(SpecraText, {
                                    size: "caption",
                                    tone: "muted",
                                    children: "No Capture Plan areas yet. Add areas on Profile & plan to pick them here."
                                }) : /*#__PURE__*/ _jsx("div", {
                                    className: "flex flex-wrap gap-x-4 gap-y-1.5",
                                    children: planAreas.map((a)=>/*#__PURE__*/ _jsx(SpecraCheckboxField, {
                                            label: a.name,
                                            controlProps: {
                                                id: `capture-review-area-${a.id}`,
                                                checked: areas.includes(a.name),
                                                disabled: create.isPending,
                                                onCheckedChange: (checked)=>setAreas((cur)=>checked === true ? [
                                                            ...cur.filter((n)=>n !== a.name),
                                                            a.name
                                                        ] : cur.filter((n)=>n !== a.name))
                                            }
                                        }, a.id))
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "Readiness",
                            htmlFor: "capture-review-readiness",
                            children: /*#__PURE__*/ _jsx(SpecraSelect, {
                                id: "capture-review-readiness",
                                value: readiness,
                                onChange: (e)=>setReadiness(e.target.value),
                                disabled: create.isPending,
                                children: READINESS_OPTIONS.map((o)=>/*#__PURE__*/ _jsx("option", {
                                        value: o.value,
                                        children: o.label
                                    }, o.value))
                            })
                        }),
                        linkable.length > 0 && /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "Linked go/no-go record (optional)",
                            htmlFor: "capture-review-link",
                            children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                                id: "capture-review-link",
                                value: goNoGoRecordId,
                                onChange: (e)=>setGoNoGoRecordId(e.target.value),
                                disabled: create.isPending,
                                children: [
                                    /*#__PURE__*/ _jsx("option", {
                                        value: "",
                                        children: "None"
                                    }),
                                    linkable.map((r)=>/*#__PURE__*/ _jsxs("option", {
                                            value: r.id,
                                            children: [
                                                r.outcome,
                                                " — ",
                                                r.scopeLabel || 'Pursuit'
                                            ]
                                        }, r.id))
                                ]
                            })
                        }),
                        createError && /*#__PURE__*/ _jsx("p", {
                            className: "text-sm text-danger",
                            role: "alert",
                            children: createError
                        })
                    ]
                })
            })
        ]
    });
}
